import React, { useState, useEffect, useCallback } from 'react';
import { apiFetch } from '../../apiClient';
import { Profile } from '../../hooks/useAuth';
import { format } from 'date-fns';
import {
  Calendar, AlertCircle, Check, X, CheckCircle2,
  MessageCircle, Printer, Search, Filter,
} from 'lucide-react';
import { Card } from '../ui/Card';
import { Badge, BookingStatus } from '../ui/Badge';
import { EmptyState } from '../ui/EmptyState';

interface Booking {
  id: string;
  service_name: string;
  service_duration: number;
  guest_name: string | null;
  guest_email: string | null;
  guest_phone: string | null;
  start_time: string;
  end_time: string;
  status: string;
  payment_status: string;
  total_price: number;
  notes: string | null;
  created_at: string;
}

const PAYMENT_STYLES: Record<string, string> = {
  unpaid:  'bg-line text-ink-muted',
  pending: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
  paid:    'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300',
  failed:  'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300',
};

const KNOWN_STATUSES = ['pending', 'confirmed', 'completed', 'cancelled'];

const fmtTime = (d: Date) => format(d, 'h:mm a');
const bookingRef = (id: string) => id.slice(0, 8).toUpperCase();

const toWaNumber = (phone: string): string => {
  const d = phone.replace(/\D/g, '');
  if (d.startsWith('60')) return d;
  if (d.startsWith('0')) return '6' + d;
  return '60' + d;
};

const sendWhatsApp = (booking: Booking, businessName: string) => {
  if (!booking.guest_phone) { alert('No phone number on record for this booking.'); return; }
  const ref = bookingRef(booking.id);
  const date = format(new Date(booking.start_time), 'EEEE, d MMMM yyyy');
  const time = `${fmtTime(new Date(booking.start_time))} – ${fmtTime(new Date(booking.end_time))}`;

  const messages: Record<string, string> = {
    pending: [
      `Hi ${booking.guest_name || 'there'}! We have received your booking at *${businessName}*.`,
      ``,
      `*Service:* ${booking.service_name}`,
      `*Date:* ${date}`,
      `*Time:* ${time}`,
      `*Reference:* ${ref}`,
      ``,
      `We will confirm your appointment shortly. Thank you for your patience! 🙏`,
    ].join('\n'),
    confirmed: [
      `Hi ${booking.guest_name || 'there'}! ✅ Your booking at *${businessName}* is *CONFIRMED*.`,
      ``,
      `*Service:* ${booking.service_name}`,
      `*Date:* ${date}`,
      `*Time:* ${time}`,
      `*Reference:* ${ref}`,
      `*Amount:* RM ${Number(booking.total_price).toFixed(2)}`,
      ``,
      `Please arrive on time. See you soon! 😊`,
    ].join('\n'),
    completed: [
      `Hi ${booking.guest_name || 'there'}! Thank you for visiting *${businessName}*.`,
      ``,
      `We hope you enjoyed your *${booking.service_name}* session on ${date}.`,
      ``,
      `We look forward to seeing you again! 🌟`,
    ].join('\n'),
  };

  const msg = messages[booking.status] ?? messages.confirmed;
  window.open(`https://wa.me/${toWaNumber(booking.guest_phone)}?text=${encodeURIComponent(msg)}`, '_blank');
};

const printConfirmation = (booking: Booking, businessName: string) => {
  const ref = bookingRef(booking.id);
  const date = format(new Date(booking.start_time), 'EEEE, d MMMM yyyy');
  const timeStart = fmtTime(new Date(booking.start_time));
  const timeEnd = fmtTime(new Date(booking.end_time));
  const statusLabel = booking.status.toUpperCase();
  const statusColor = booking.status === 'confirmed' ? '#16a34a'
    : booking.status === 'completed' ? '#1d4ed8'
    : '#b45309';

  const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8" />
  <title>Booking Confirmation — ${ref}</title>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { font-family: 'Georgia', serif; background: #fdfcf0; display: flex; align-items: center; justify-content: center; min-height: 100vh; padding: 40px 20px; }
    .ticket { width: 480px; background: #fff; box-shadow: 0 8px 40px rgba(15,23,42,0.12); }
    .header { background: #0f172a; color: #fdfcf0; padding: 32px; text-align: center; }
    .tag { font-size: 9px; letter-spacing: 5px; text-transform: uppercase; color: #d4af37; margin-bottom: 8px; }
    .company { font-size: 26px; font-weight: 900; letter-spacing: -1px; }
    .subtitle { font-size: 10px; letter-spacing: 3px; text-transform: uppercase; color: #94a3b8; margin-top: 6px; }
    .ref-bar { background: #f7f5e8; padding: 18px 32px; border-bottom: 2px dashed #d6d3c4; display: flex; justify-content: space-between; align-items: center; }
    .ref-label { font-size: 9px; letter-spacing: 3px; text-transform: uppercase; color: #64748b; font-family: sans-serif; }
    .ref-value { font-size: 22px; font-family: 'Courier New', monospace; font-weight: 900; letter-spacing: 3px; color: #0f172a; }
    .body { padding: 24px 32px; }
    .row { display: flex; justify-content: space-between; align-items: flex-start; padding: 12px 0; border-bottom: 1px solid #f0eee0; gap: 16px; }
    .row:last-child { border-bottom: none; }
    .lbl { font-size: 9px; letter-spacing: 2px; text-transform: uppercase; color: #94a3b8; font-family: sans-serif; padding-top: 2px; flex-shrink: 0; }
    .val { font-weight: 700; font-size: 13px; color: #0f172a; text-align: right; }
    .status-pill { display: inline-block; padding: 3px 12px; border-radius: 2px; font-size: 10px; font-weight: 900; letter-spacing: 2px; text-transform: uppercase; font-family: sans-serif; color: ${statusColor}; background: ${statusColor}18; border: 1px solid ${statusColor}40; }
    .divider { border: none; border-top: 2px dashed #d6d3c4; margin: 0 32px; }
    .amount-bar { background: #0f172a; color: #fdfcf0; padding: 20px 32px; display: flex; justify-content: space-between; align-items: center; }
    .amt-lbl { font-size: 9px; letter-spacing: 3px; text-transform: uppercase; color: #d4af37; font-family: sans-serif; }
    .amt-val { font-size: 30px; font-weight: 900; letter-spacing: -1px; color: #d4af37; }
    .footer { padding: 16px 32px; text-align: center; font-size: 9px; color: #b4b2a9; letter-spacing: 2px; text-transform: uppercase; font-family: sans-serif; }
    @media print { body { background: white; padding: 0; } .ticket { box-shadow: none; width: 100%; } }
  </style>
</head>
<body>
  <div class="ticket">
    <div class="header">
      <div class="tag">Booking Confirmation</div>
      <div class="company">${businessName}</div>
      <div class="subtitle">Powered by NexSlot</div>
    </div>
    <div class="ref-bar">
      <span class="ref-label">Reference No.</span>
      <span class="ref-value">${ref}</span>
    </div>
    <div class="body">
      <div class="row"><span class="lbl">Customer</span><span class="val">${booking.guest_name || '—'}</span></div>
      <div class="row"><span class="lbl">Phone</span><span class="val">${booking.guest_phone || '—'}</span></div>
      <div class="row"><span class="lbl">Service</span><span class="val">${booking.service_name} (${booking.service_duration} min)</span></div>
      <div class="row"><span class="lbl">Date</span><span class="val">${date}</span></div>
      <div class="row"><span class="lbl">Time</span><span class="val">${timeStart} – ${timeEnd}</span></div>
      <div class="row"><span class="lbl">Status</span><span class="val"><span class="status-pill">${statusLabel}</span></span></div>
      <div class="row"><span class="lbl">Payment</span><span class="val">${booking.payment_status.toUpperCase()}</span></div>
    </div>
    <hr class="divider" />
    <div class="amount-bar">
      <span class="amt-lbl">Total Amount</span>
      <span class="amt-val">RM ${Number(booking.total_price).toFixed(2)}</span>
    </div>
    <div class="footer">Generated on ${format(new Date(), 'dd MMM yyyy, h:mm a')}</div>
  </div>
  <script>window.onload = () => { window.print(); }<\/script>
</body>
</html>`;

  const win = window.open('', '_blank', 'width=560,height=780');
  if (win) { win.document.write(html); win.document.close(); }
};

const StatusBadge: React.FC<{ status: string }> = ({ status }) => (
  KNOWN_STATUSES.includes(status)
    ? <Badge status={status as BookingStatus}>{status}</Badge>
    : <Badge>{status}</Badge>
);

const PaymentBadge: React.FC<{ status: string }> = ({ status }) => (
  <span className={`inline-flex items-center px-2.5 py-1 rounded-sm label-caps ${PAYMENT_STYLES[status] ?? 'bg-line text-ink-muted'}`}>
    {status}
  </span>
);

const BookingActions: React.FC<{
  booking: Booking;
  businessName: string;
  actionLoading: string | null;
  updateStatus: (id: string, status: string) => void;
}> = ({ booking, businessName, actionLoading, updateStatus }) => (
  <div className="flex items-center gap-1.5">
    {booking.status === 'pending' && (
      <button onClick={() => updateStatus(booking.id, 'confirmed')}
        disabled={actionLoading === booking.id + 'confirmed'} title="Confirm booking"
        className="p-2 bg-blue-50 text-blue-600 dark:bg-blue-900/30 dark:text-blue-300 rounded-sm hover:opacity-80 transition-opacity disabled:opacity-40">
        <Check size={15} />
      </button>
    )}
    {booking.status === 'confirmed' && (
      <button onClick={() => updateStatus(booking.id, 'completed')}
        disabled={actionLoading === booking.id + 'completed'} title="Mark completed"
        className="p-2 bg-green-50 text-green-600 dark:bg-green-900/30 dark:text-green-300 rounded-sm hover:opacity-80 transition-opacity disabled:opacity-40">
        <CheckCircle2 size={15} />
      </button>
    )}
    {(booking.status === 'pending' || booking.status === 'confirmed') && (
      <button onClick={() => { if (window.confirm('Cancel this booking?')) updateStatus(booking.id, 'cancelled'); }}
        disabled={actionLoading === booking.id + 'cancelled'} title="Cancel booking"
        className="p-2 bg-red-50 text-red-500 dark:bg-red-900/30 dark:text-red-300 rounded-sm hover:opacity-80 transition-opacity disabled:opacity-40">
        <X size={15} />
      </button>
    )}
    {booking.status !== 'cancelled' && (
      <>
        <div className="w-px h-5 bg-line mx-0.5" />
        <button onClick={() => sendWhatsApp(booking, businessName)}
          title="Send confirmation to customer via WhatsApp"
          className="p-2 bg-green-50 text-[#25D366] dark:bg-green-900/30 rounded-sm hover:opacity-80 transition-opacity">
          <MessageCircle size={15} />
        </button>
        <button onClick={() => printConfirmation(booking, businessName)}
          title="Print or save as PDF"
          className="p-2 bg-surface text-ink-muted border border-line rounded-sm hover:text-ink transition-colors">
          <Printer size={15} />
        </button>
      </>
    )}
  </div>
);

export const BookingManagement: React.FC<{ profile: Profile }> = () => {
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [businessName, setBusinessName] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('');
  const [refFilter, setRefFilter] = useState('');

  const fetchBookings = useCallback(async () => {
    try {
      setLoading(true);
      const [bookData, profileData] = await Promise.all([
        apiFetch('/provider/bookings'),
        apiFetch('/provider/profile'),
      ]);
      if (!bookData.success) throw new Error(bookData.message);
      setBookings(bookData.bookings);
      if (profileData.success) setBusinessName(profileData.profile.business_name || '');
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchBookings(); }, [fetchBookings]);

  const updateStatus = async (id: string, status: string) => {
    setActionLoading(id + status);
    setError(null);
    try {
      const data = await apiFetch(`/provider/bookings/${id}/status`, {
        method: 'PATCH',
        body: JSON.stringify({ status }),
      });
      if (!data.success) throw new Error(data.message);
      fetchBookings();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setActionLoading(null);
    }
  };

  const filtered = bookings.filter(b => {
    const matchesStatus = statusFilter === 'all' || b.status === statusFilter;
    const matchesDate = !dateFilter || new Date(b.start_time).toLocaleDateString('en-CA') === dateFilter;
    const matchesRef = !refFilter || bookingRef(b.id).includes(refFilter.toUpperCase().replace('#', ''));
    return matchesStatus && matchesDate && matchesRef;
  });

  if (loading) return (
    <div className="p-12 flex items-center justify-center">
      <p className="label-caps text-ink-faint">Loading bookings...</p>
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto p-6 md:p-10 space-y-6">
      <div>
        <h2 className="display text-2xl md:text-3xl text-ink">Bookings</h2>
        <p className="label-caps text-ink-faint mt-2">
          {filtered.length} of {bookings.length} booking{bookings.length !== 1 ? 's' : ''}
        </p>
      </div>

      {/* Filter bar */}
      <Card className="p-4 flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[180px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-faint" size={14} />
          <input
            type="text"
            placeholder="Search reference..."
            value={refFilter}
            onChange={e => setRefFilter(e.target.value)}
            className="w-full pl-9 pr-4 min-h-[44px] bg-surface border border-line rounded-sm text-xs font-semibold text-ink placeholder:text-ink-faint outline-none focus:border-accent transition-colors uppercase"
          />
        </div>

        <div className="relative">
          <input
            type="date"
            value={dateFilter}
            onChange={e => setDateFilter(e.target.value)}
            className="pl-3 pr-8 min-h-[44px] bg-surface border border-line rounded-sm text-xs font-semibold text-ink outline-none focus:border-accent transition-colors"
          />
          {dateFilter && (
            <button onClick={() => setDateFilter('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-ink-faint hover:text-ink">
              <X size={12} />
            </button>
          )}
        </div>

        <div className="flex items-center gap-2">
          <Filter size={14} className="text-ink-faint" />
          <select
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}
            className="bg-surface border border-line rounded-sm px-3 min-h-[44px] text-xs font-semibold text-ink outline-none focus:border-accent transition-colors"
          >
            {['all', 'pending', 'confirmed', 'completed', 'cancelled'].map(s => (
              <option key={s} value={s}>{s === 'all' ? 'All statuses' : s.charAt(0).toUpperCase() + s.slice(1)}</option>
            ))}
          </select>
        </div>
      </Card>

      {error && (
        <div className="p-4 flex items-center gap-3 text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/30 rounded-sm border border-red-200 dark:border-red-900 text-xs font-semibold">
          <AlertCircle size={16} className="shrink-0" /> {error}
        </div>
      )}

      {filtered.length === 0 ? (
        <EmptyState icon={Calendar} title="No bookings found" hint="Try adjusting your filters" />
      ) : (
        <>
          {/* Mobile — booking cards */}
          <div className="space-y-4 md:hidden">
            {filtered.map(booking => (
              <Card accent key={booking.id} className="p-5 space-y-4">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <span className="font-mono font-semibold text-xs text-ink tracking-wider bg-surface border border-line px-2 py-1 rounded-sm">
                      #{bookingRef(booking.id)}
                    </span>
                    <p className="display text-lg text-ink mt-2">{booking.service_name}</p>
                    <p className="text-xs text-ink-muted">{booking.service_duration} min · {booking.guest_name || '—'}</p>
                  </div>
                  <p className="display text-lg text-ink shrink-0">RM {Number(booking.total_price).toFixed(2)}</p>
                </div>
                <div className="flex items-center justify-between text-xs text-ink-muted">
                  <span>
                    {format(new Date(booking.start_time), 'dd MMM yyyy')} · {fmtTime(new Date(booking.start_time))} – {fmtTime(new Date(booking.end_time))}
                  </span>
                </div>
                <div className="flex items-center justify-between gap-3 pt-3 border-t border-line">
                  <div className="flex gap-2">
                    <StatusBadge status={booking.status} />
                    <PaymentBadge status={booking.payment_status} />
                  </div>
                  <BookingActions booking={booking} businessName={businessName}
                    actionLoading={actionLoading} updateStatus={updateStatus} />
                </div>
              </Card>
            ))}
          </div>

          {/* Desktop — table */}
          <Card className="hidden md:block overflow-x-auto">
            <table className="w-full text-left border-collapse min-w-[900px]">
              <thead className="bg-inverse">
                <tr>
                  {['Reference', 'Guest', 'Service', 'Date & time', 'Status', 'Payment', 'Amount', 'Actions'].map((h, i) => (
                    <th key={h} className={`px-5 py-4 label-caps text-slate-400 ${i >= 4 && i <= 5 ? 'text-center' : ''} ${i === 7 ? 'text-right' : ''}`}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {filtered.map(booking => (
                  <tr key={booking.id} className="hover:bg-surface transition-colors">
                    <td className="px-5 py-5">
                      <span className="font-mono font-semibold text-xs text-ink tracking-wider bg-surface border border-line px-2 py-1 rounded-sm">
                        #{bookingRef(booking.id)}
                      </span>
                    </td>
                    <td className="px-5 py-5">
                      <p className="text-sm font-semibold text-ink">{booking.guest_name || '—'}</p>
                      <p className="text-xs text-ink-faint mt-0.5">{booking.guest_email || ''}</p>
                      {booking.guest_phone && <p className="text-xs text-ink-faint">{booking.guest_phone}</p>}
                    </td>
                    <td className="px-5 py-5">
                      <p className="text-sm font-semibold text-ink">{booking.service_name}</p>
                      <p className="text-xs text-ink-faint mt-0.5">{booking.service_duration} min</p>
                    </td>
                    <td className="px-5 py-5">
                      <p className="text-sm font-semibold text-ink">{format(new Date(booking.start_time), 'dd MMM yyyy')}</p>
                      <p className="text-xs text-ink-faint mt-0.5">
                        {fmtTime(new Date(booking.start_time))} – {fmtTime(new Date(booking.end_time))}
                      </p>
                    </td>
                    <td className="px-5 py-5 text-center"><StatusBadge status={booking.status} /></td>
                    <td className="px-5 py-5 text-center"><PaymentBadge status={booking.payment_status} /></td>
                    <td className="px-5 py-5">
                      <p className="display text-lg text-ink">RM {Number(booking.total_price).toFixed(2)}</p>
                    </td>
                    <td className="px-5 py-5">
                      <div className="flex justify-end">
                        <BookingActions booking={booking} businessName={businessName}
                          actionLoading={actionLoading} updateStatus={updateStatus} />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </>
      )}
    </div>
  );
};
